/**
 * Trade setting templates.
 * Each template populates: savedAlgos, riskSize, maxRiskSize, contractLimit,
 * stackTrades, singleDirectionTrading, profitTarget, dailyLossLimit, dailyProfitLimit.
 *
 * savedAlgos format: ["AlgoName_TICKER_Timeframe", ...]
 * Use '' (empty string) for numeric fields you want left blank (e.g. profitTarget: '').
 *
 * To add a new template, just add another entry to the array below.
 *
 * Multi-group templates: set type: 'multi-group' and provide a `groups` array
 * instead of `settings`. Each group entry needs a `groupName` and the same
 * settings fields. Existing groups with the same name will be overwritten.
 *
 * Reference groups: use { groupName: 'Name', isReference: true, templateName: 'Poseidon Conservative $50k' }
 * to apply an existing single-group template's settings to that group name.
 */

export const TRADE_TEMPLATES = [
    {
        name: 'Prop Strategy A $50k',
        description: 'Poseidon All',
        type: 'multi-group',
        //Average days to pass = 10
        groups: [
            {
                groupName: 'Prop Strategy A $50k',
                isReference: true,
                templateName: 'Poseidon Aggressive $50k',
            }
        ]
    },
    {
        name: 'Prop Strategy A $50k Conservative',
        description: 'Poseidon Medium Conservative',
        type: 'multi-group',
        groups: [
            {
                groupName: 'Prop Strategy A $50k Conservative',
                isReference: true,
                templateName: 'Poseidon Conservative $50k',
            }
        ]
    },
    {
        name: 'Prop Strategy B $50k',
        description: 'Fortuna Conservative',
        type: 'multi-group',
        groups: [
            {
                groupName: 'Prop Strategy B $50k',
                isReference: true,
                templateName: 'Fortuna Conservative $50k',
            }
        ]
    },
    {
        name: 'Prop Strategy C $50k', //Average days = 15
        description: 'Poseidon + Fortuna',
        type: 'multi-group',
        groups: [
            {
                name: 'Prop Strategy C Group A',
                description: 'Poseidon Conservative',
                settings: {
                    riskSize: 400,
                    maxRiskSize: 1000,
                    profitTarget: '',
                    contractLimit: 100,
                    dailyLossLimit: 1500,
                    dailyProfitLimit: 3000,
                    stackTrades: true,
                    singleDirectionTrading: true,
                    savedAlgos: [
                        "Poseidon_MGC_5m-poseidon",
                        "Poseidon_MNQ_5m-poseidon"
                    ]
                },
            },
            {
                name: 'Prop Strategy C Group B',
                description: 'Fortuna Conservative',
                settings: {
                    riskSize: 100,
                    maxRiskSize: 500,
                    profitTarget: 2,
                    contractLimit: 100,
                    dailyLossLimit: 1500,
                    dailyProfitLimit: 3000,
                    stackTrades: true,
                    singleDirectionTrading: true,
                    maxTradesPerDay: 2,
                    savedAlgos: [
                        "Fortuna_MNQ_5m-fortuna",
                    ]
                },
            },
        ]
    },
    {
        name: 'Poseidon Conservative $50k',
        description: 'Poseidon balanced MES and MNQ',
        settings: {
            riskSize: 500,
            maxRiskSize: 5000,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 1500,
            dailyProfitLimit: 3000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Poseidon_MES_5m-poseidon",
                "Poseidon_MGC_5m-poseidon"
            ]
        },
    },
    {
        name: 'Poseidon Aggressive $50k',
        description: 'Higher risk Poseidon MES, MNQ, and MGC',
        settings: {
            riskSize: 500,
            maxRiskSize: 5000,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 1500,
            dailyProfitLimit: 3000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Poseidon_MES_5m-poseidon",
                "Poseidon_MGC_5m-poseidon",
                "Poseidon_MNQ_5m-poseidon"
            ]
        },
    },
    {
        name: 'Atlas Conservative $50k',
        description: 'Atlas Conservative MNQ',
        settings: {
            riskSize: 500,
            maxRiskSize: 5000,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 3000,
            dailyProfitLimit: 4000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Atlas_MNQ_10m-atlas",
            ]
        },
    },
    {
        name: 'Atlas Aggressive $50k',
        description: 'Atlas Aggressive MES and MNQ',
        settings: {
            riskSize: 750,
            maxRiskSize: 5000,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 3000,
            dailyProfitLimit: 4000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Atlas_MES_10m-atlas",
                "Atlas_MNQ_10m-atlas",
            ]
        },
    },
    {
        name: 'Ares Conservative $50k',
        description: 'Ares Conservative',
        settings: {
            riskSize: 250,
            maxRiskSize: 500,
            profitTarget: 2,
            contractLimit: 100,
            dailyLossLimit: 1000,
            dailyProfitLimit: 1000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Ares_MGC_1m-ares",
                "Ares_MGC_1m-ares-reversal",
            ]
        },
    },
    {
        name: 'Ares Aggressive $50k',
        description: 'Ares Aggressive',
        settings: {
            riskSize: 500,
            maxRiskSize: 750,
            profitTarget: 2,
            contractLimit: 100,
            dailyLossLimit: 1500,
            dailyProfitLimit: 2000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Ares_MGC_1m-ares",
                "Ares_MGC_1m-ares-reversal",
            ]
        },
    },
    {
        name: 'Fortuna Conservative $50k',
        description: 'Fortuna Conservative',
        settings: {
            riskSize: 200,
            maxRiskSize: 500,
            profitTarget: 2,
            contractLimit: 100,
            dailyLossLimit: 440,
            dailyProfitLimit: 720,
            stackTrades: true,
            singleDirectionTrading: true,
            maxTradesPerDay: 2,
            savedAlgos: [
                "Fortuna_MNQ_5m-fortuna",
            ]
        },
    },
    {
        name: 'Fortuna Aggressive $50k',
        description: 'Fortuna Aggressive',
        settings: {
            riskSize: 400,
            maxRiskSize: 750,
            profitTarget: 2,
            contractLimit: 10,
            dailyLossLimit: 880,
            dailyProfitLimit: 1440,
            stackTrades: true,
            singleDirectionTrading: true,
            maxTradesPerDay: 2,
            savedAlgos: [
                "Fortuna_MNQ_5m-fortuna",
            ]
        },
    },
    {
        name: 'Kratos Conservative $50k',
        description: 'Kratos Conservative',
        settings: {
            riskSize: 500,
            maxRiskSize: 1500,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 1500,
            dailyProfitLimit: 3000,
            stackTrades: false,
            singleDirectionTrading: true,
            savedAlgos: [
                "Kratos_MNQ_5m-kratos",
            ]
        },
    },
    {
        name: 'Kratos Aggressive $50k',
        description: 'Kratos Aggressive',
        settings: {
            riskSize: 750,
            maxRiskSize: 1500,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 1500,
            dailyProfitLimit: 3000,
            stackTrades: false,
            singleDirectionTrading: true,
            savedAlgos: [
                "Kratos_MNQ_5m-kratos",
            ]
        },
    },
    {
        name: 'Market Power and Double Dip $10k',
        description: 'Market Power and Double Dip',
        settings: {
            riskSize: 1000,
            maxRiskSize: 2500,
            profitTarget: '',
            contractLimit: '',
            dailyLossLimit: '',
            dailyProfitLimit: '',
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "MarketPower_SPX_1DTE",
                "DoubleDip_SPX_0DTE",
            ]
        },
    },
];
